import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut, signInWithPopup } from 'firebase/auth';
import { auth, provider, db, analytics } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { logEvent } from 'firebase/analytics';

const MARKETS = [
  'Global Standard',
  'India',
  'US & Canada',
  'UK & Ireland',
  'Australia & New Zealand',
  'European Professional',
  'Gulf Professional',
];

const LOADING_MESSAGES = [
  'Analysing your CV...',
  'Matching keywords to job description...',
  'Optimising for ATS systems...',
  'Crafting your cover letter...',
  'Generating interview tips...',
  'Building insights report...',
  'Almost there...',
];

// localStorage key tracking whether this browser has used its 1 free guest optimisation.
// Namespaced to avoid clashing with anything else stored under the app's origin.
const GUEST_USED_KEY = 'applyedge_guest_used';

export default function AppPage() {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const location = useLocation();
  const [credits, setCredits] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const [jdText, setJdText] = useState('');
  const [market, setMarket] = useState('Global Standard');
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');
  const [error, setError] = useState('');
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signInLoading, setSignInLoading] = useState(false);
  const [guestUsed, setGuestUsed] = useState(false);
  const fileInputRef = useRef();
  const loadingInterval = useRef();

  useEffect(() => {
    if (user) fetchCredits();
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    if (payment === 'success') {
      fetchCredits();
      logEvent(analytics, 'purchase_completed');
      window.history.replaceState({}, '', '/app');
    }
  }, []);

  // Pre-fill from history re-optimise
  useEffect(() => {
    if (location.state?.prefillJdText) {
      setJdText(location.state.prefillJdText);
    }
    if (location.state?.prefillMarket) {
      setMarket(location.state.prefillMarket);
    }
  }, []);

  // Sync guest-used flag from localStorage on mount
  useEffect(() => {
    setGuestUsed(localStorage.getItem(GUEST_USED_KEY) === 'true');
  }, []);

  const fetchCredits = async () => {
    try {
      const docRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setCredits((data.usage_limit || 0) - (data.usage_count || 0));
      } else {
        setCredits(3);
      }
    } catch (e) {
      setCredits(0);
    }
  };

  const startLoadingMessages = () => {
    let i = 0;
    setLoadingMsg(LOADING_MESSAGES[0]);
    loadingInterval.current = setInterval(() => {
      i = (i + 1) % LOADING_MESSAGES.length;
      setLoadingMsg(LOADING_MESSAGES[i]);
    }, 4000);
  };

  const stopLoadingMessages = () => {
    clearInterval(loadingInterval.current);
  };

  const handleGoogleSignIn = async () => {
    setSignInLoading(true);
    try {
      await signInWithPopup(auth, provider);
      setShowSignInModal(false);
      logEvent(analytics, 'login_success', {
        guest_conversion: localStorage.getItem(GUEST_USED_KEY) === 'true'
      });
      setTimeout(() => handleSubmitAfterSignIn(), 500);
    } catch (err) {
      setSignInLoading(false);
    }
  };

  const handleSubmitAfterSignIn = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    setShowSignInModal(false);
    setError('');
    setLoading(true);
    startLoadingMessages();

    try {
      const token = await currentUser.getIdToken();
      const formData = new FormData();
      formData.append('cv_file', cvFile);
      formData.append('jd_text', jdText);
      formData.append('target_market', market);
      // Read directly from localStorage (not React state) to avoid any stale
      // closure risk from the setTimeout in handleGoogleSignIn.
      formData.append('guest_conversion', localStorage.getItem(GUEST_USED_KEY) === 'true' ? 'true' : 'false');

      const response = await fetch('https://api.theapplyedge.com/api/v1/process-web', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Analysis failed. Please try again.');
      }

      const data = await response.json();
      stopLoadingMessages();
      setLoading(false);
      await fetchCredits();
      logEvent(analytics, 'cv_optimisation_complete');
      navigate('/results', { state: { results: data } });
    } catch (err) {
      stopLoadingMessages();
      setLoading(false);
      setError(err.message || 'Something went wrong. Please try again.');
    }
  };

  // Guest submit: no auth, no modal. Hits /process-guest, which is rate
  // limited server-side to 1 use per IP per UTC day. A 429 here means the
  // guest_used flag was cleared locally (or this is a different device on
  // the same IP) but the server still remembers — we sync the local flag
  // and fall through to the sign-in modal rather than showing a raw error.
  const handleGuestSubmit = async () => {
    setError('');
    setLoading(true);
    startLoadingMessages();

    try {
      const formData = new FormData();
      formData.append('cv_file', cvFile);
      formData.append('jd_text', jdText);
      formData.append('target_market', market);

      const response = await fetch('https://api.theapplyedge.com/api/v1/process-guest', {
        method: 'POST',
        body: formData,
      });

      if (response.status === 429) {
        localStorage.setItem(GUEST_USED_KEY, 'true');
        setGuestUsed(true);
        stopLoadingMessages();
        setLoading(false);
        logEvent(analytics, 'guest_wall_shown');
        setShowSignInModal(true);
        return;
      }

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Analysis failed. Please try again.');
      }

      const data = await response.json();
      stopLoadingMessages();
      setLoading(false);
      localStorage.setItem(GUEST_USED_KEY, 'true');
      setGuestUsed(true);
      logEvent(analytics, 'guest_cv_optimisation_complete');
      navigate('/results', { state: { results: data } });
    } catch (err) {
      stopLoadingMessages();
      setLoading(false);
      setError(err.message || 'Something went wrong. Please try again.');
    }
  };

  const handleSubmit = async () => {
    if (!cvFile) return setError('Please upload your CV.');
    if (!jdText.trim()) return setError('Please paste the job description.');

    if (!user) {
      if (!guestUsed) {
        handleGuestSubmit();
      } else {
        logEvent(analytics, 'guest_wall_shown');
        setShowSignInModal(true);
      }
      return;
    }

    if (credits <= 0) return setError('No credits remaining. Please upgrade.');

    setError('');
    setLoading(true);
    startLoadingMessages();

    try {
      const token = await user.getIdToken();
      const formData = new FormData();
      formData.append('cv_file', cvFile);
      formData.append('jd_text', jdText);
      formData.append('target_market', market);
      // Covers the case where a guest signs in via the navbar "Sign In" link
      // (rather than the forced modal) and then submits normally here.
      // Harmless to always send: the backend only honours this flag when
      // creating a brand-new user doc, and ignores it for existing accounts.
      formData.append('guest_conversion', localStorage.getItem(GUEST_USED_KEY) === 'true' ? 'true' : 'false');

      const response = await fetch('https://api.theapplyedge.com/api/v1/process-web', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Analysis failed. Please try again.');
      }

      const data = await response.json();
      stopLoadingMessages();
      setLoading(false);
      await fetchCredits();
      logEvent(analytics, 'cv_optimisation_complete');
      navigate('/results', { state: { results: data } });
    } catch (err) {
      stopLoadingMessages();
      setLoading(false);
      setError(err.message || 'Something went wrong. Please try again.');
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    navigate('/');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCvFile(file);
      logEvent(analytics, 'resume_uploaded');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0D0D1A' }}>

      {/* Sign In Modal — only ever shown after the guest's 1 free use is exhausted */}
      {showSignInModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: '#1A1A2E', border: '1px solid #2A2A4A',
            borderRadius: 20, padding: '40px 32px', maxWidth: 400, width: '100%',
            textAlign: 'center'
          }}>
            <div style={{
              width: 56, height: 56, background: 'linear-gradient(135deg, #0F7DFF, #00D4AA)',
              borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, margin: '0 auto 20px'
            }}>⚡</div>
            <h2 style={{
              fontFamily: 'Space Grotesk, sans-serif', fontSize: 22,
              fontWeight: 700, marginBottom: 8
            }}>Sign in to continue</h2>
            <p style={{ color: '#8888AA', fontSize: 14, marginBottom: 28, lineHeight: 1.5 }}>
              You've used your free guest optimisation. Create a free account to get 2 more CV optimisations. No credit card needed.
            </p>
            <button
              onClick={handleGoogleSignIn}
              disabled={signInLoading}
              style={{
                width: '100%', padding: '14px', borderRadius: 12,
                border: '1px solid #2A2A4A', background: '#fff',
                color: '#000', fontFamily: 'Inter, sans-serif', fontWeight: 600,
                fontSize: 15, cursor: signInLoading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                opacity: signInLoading ? 0.7 : 1, marginBottom: 12
              }}>
              <img src="https://www.google.com/favicon.ico" alt="Google" width={18} height={18} />
              {signInLoading ? 'Signing in...' : 'Continue with Google'}
            </button>
          </div>
        </div>
      )}

      {/* Navbar */}
      <nav style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '16px 40px', borderBottom: '1px solid #2A2A4A'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/app_icon.png" alt="ApplyEdge" style={{ width: 34, height: 34, borderRadius: 9 }} />
          <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: 18 }}>ApplyEdge</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {user ? (
            <>
              <div style={{
                background: '#1A1A2E', border: '1px solid #2A2A4A',
                borderRadius: 20, padding: '6px 14px', fontSize: 13, color: '#00D4AA', fontWeight: 600
              }}>
                {credits !== null ? `${credits} credits` : '...'}
              </div>
              <div style={{ color: '#8888AA', fontSize: 13 }}>{user?.displayName}</div>
              <button onClick={() => navigate('/upgrade')} style={{
                background: 'linear-gradient(135deg, #0F7DFF, #00D4AA)',
                border: 'none', borderRadius: 8, padding: '6px 14px', color: '#fff',
                fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, cursor: 'pointer'
              }}>Upgrade</button>
              <button onClick={() => navigate('/history')} style={{
                background: 'transparent', border: '1px solid #2A2A4A',
                borderRadius: 8, padding: '6px 14px', color: '#8888AA',
                fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, cursor: 'pointer'
              }}>History</button>
              <button onClick={handleSignOut} style={{
                background: 'transparent', border: '1px solid #2A2A4A',
                borderRadius: 8, padding: '6px 14px', color: '#8888AA',
                fontSize: 13, cursor: 'pointer'
              }}>Sign Out</button>
            </>
          ) : (
            <button onClick={() => navigate('/login')} style={{
              background: 'linear-gradient(135deg, #0F7DFF, #00D4AA)',
              border: 'none', borderRadius: 8, padding: '6px 14px', color: '#fff',
              fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, cursor: 'pointer'
            }}>Sign In</button>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px' }}>
        <h1 style={{
          fontFamily: 'Space Grotesk, sans-serif', fontSize: 32,
          fontWeight: 700, marginBottom: 8
        }}>Optimise Your CV</h1>
        <p style={{ color: '#8888AA', marginBottom: 40 }}>Upload your CV and paste the job description to get started.</p>

        {/* Guest banner — copy adapts once the free guest use is spent */}
        {!user && (
          <div style={{
            background: '#1A1A2E', border: '1px solid #2A2A4A',
            borderRadius: 12, padding: '14px 20px', marginBottom: 24,
            display: 'flex', alignItems: 'center', gap: 10
          }}>
            <span style={{ fontSize: 18 }}>🎁</span>
            <span style={{ color: '#8888AA', fontSize: 14 }}>
              {guestUsed
                ? "You've used your free guest optimisation. Sign in for 2 more — no credit card needed."
                : '1 free optimisation as a guest — no sign-in needed. Sign in afterwards for 2 more, free.'}
            </span>
          </div>
        )}

        {/* Re-optimise banner */}
        {location.state?.prefillJdText && (
          <div style={{
            background: 'rgba(15,125,255,0.1)', border: '1px solid rgba(15,125,255,0.3)',
            borderRadius: 12, padding: '12px 20px', marginBottom: 24,
            display: 'flex', alignItems: 'center', gap: 10
          }}>
            <span style={{ fontSize: 16 }}>ℹ️</span>
            <span style={{ color: '#0F7DFF', fontSize: 14 }}>
              JD and market pre-filled from history. Upload your CV to re-optimise.
            </span>
          </div>
        )}

        {/* Two Column Layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

          {/* Left Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* CV Upload */}
            <div style={{ background: '#1A1A2E', border: '1px solid #2A2A4A', borderRadius: 16, padding: 24 }}>
              <label style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 600, fontSize: 15, display: 'block', marginBottom: 12 }}>
                Upload CV
              </label>
              <div
                onClick={() => !loading && fileInputRef.current.click()}
                style={{
                  border: '2px dashed #2A2A4A', borderRadius: 12, padding: '32px 20px',
                  textAlign: 'center', cursor: loading ? 'not-allowed' : 'pointer',
                  background: cvFile ? 'rgba(0,212,170,0.05)' : 'transparent',
                  borderColor: cvFile ? '#00D4AA' : '#2A2A4A',
                  opacity: loading ? 0.5 : 1,
                }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
                <div style={{ color: cvFile ? '#00D4AA' : '#8888AA', fontSize: 14 }}>
                  {cvFile ? cvFile.name : 'Click to upload PDF or DOCX'}
                </div>
                <div style={{ color: '#555570', fontSize: 12, marginTop: 4 }}>Max 5MB</div>
              </div>
              <input
                ref={fileInputRef} type="file" accept=".pdf,.docx"
                style={{ display: 'none' }}
                disabled={loading}
                onChange={handleFileChange}
              />
            </div>

            {/* Resume Style */}
            <div style={{ background: '#1A1A2E', border: '1px solid #2A2A4A', borderRadius: 16, padding: 24 }}>
              <label style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 600, fontSize: 15, display: 'block', marginBottom: 12 }}>
                Resume Style
              </label>
              <select
                value={market} onChange={(e) => setMarket(e.target.value)}
                disabled={loading}
                style={{
                  width: '100%', padding: '12px 14px', background: '#0D0D1A',
                  border: '1px solid #2A2A4A', borderRadius: 10, color: '#fff',
                  fontFamily: 'Inter, sans-serif', fontSize: 14,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.5 : 1,
                }}>
                {MARKETS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          {/* Right Column — JD */}
          <div style={{ background: '#1A1A2E', border: '1px solid #2A2A4A', borderRadius: 16, padding: 24 }}>
            <label style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 600, fontSize: 15, display: 'block', marginBottom: 12 }}>
              Job Description
            </label>
            <textarea
              value={jdText}
              onChange={(e) => setJdText(e.target.value.slice(0, 3000))}
              disabled={loading}
              placeholder="Paste the full job description here..."
              style={{
                width: '100%', height: 260, padding: '14px', background: '#0D0D1A',
                border: '1px solid #2A2A4A', borderRadius: 10, color: '#fff',
                fontFamily: 'Inter, sans-serif', fontSize: 14, resize: 'none',
                lineHeight: 1.6, outline: 'none',
                opacity: loading ? 0.5 : 1,
                cursor: loading ? 'not-allowed' : 'text',
              }}
            />
            <div style={{ textAlign: 'right', color: '#555570', fontSize: 12, marginTop: 6 }}>
              {jdText.length}/3000
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: 'rgba(255,107,107,0.1)', border: '1px solid #FF6B6B',
            borderRadius: 10, padding: 14, marginTop: 20, color: '#FF6B6B', fontSize: 14
          }}>{error}</div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{
            background: '#1A1A2E', border: '1px solid #2A2A4A',
            borderRadius: 10, padding: 20, marginTop: 20, textAlign: 'center'
          }}>
            <div style={{ color: '#0F7DFF', fontSize: 14, fontWeight: 500 }}>{loadingMsg}</div>
            <div style={{ color: '#555570', fontSize: 12, marginTop: 6 }}>
              Keep this tab open while analysing — this may take 25-40 seconds
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          onClick={handleSubmit} disabled={loading}
          style={{
            width: '100%', marginTop: 24, padding: '16px',
            background: loading ? '#2A2A4A' : 'linear-gradient(135deg, #0F7DFF, #00D4AA)',
            border: 'none', borderRadius: 14, color: '#fff',
            fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 16,
            cursor: loading ? 'not-allowed' : 'pointer',
            boxShadow: loading ? 'none' : '0 8px 32px rgba(15,125,255,0.35)'
          }}>
          {loading ? 'Analysing...' : 'Optimise My CV →'}
        </button>
      </div>
    </div>
  );
}